import { Box, Stack, Typography } from '@mui/material';
import { FunctionComponent } from 'react';
import { PrintableLayout } from '../../components/layout/PrintableLayout';
import { LoadingOverlay } from '../../components/LoadingOverlay';
import { ScreenTitle } from '../../components/ScreenTitle';
import { gameConfigStore, useGameConfigSelector } from '../../store/GameConfigStore';
import { QuestionSetConfig } from '../../types/GameplayTypes';
import { Environment } from '../../util/Environment';
import { useQuestionSetQuestions } from '../../util/GameConfigUtils';
import { muiStyleSheet } from '../../util/Styles';
import { friendlyDateString } from '../../util/Time';

const LOGO_PATH = '/images/Mind Clash Logo with Background.png';
const MAX_QUESTIONS = 8;

/** Printable question-only card intended to be folded in half after printing. */
export const QuestionCardScreen: FunctionComponent = () => {
	const questionSet = useGameConfigSelector(gameConfigStore.selectors.questionSet);
	const questions = useQuestionSetQuestions(questionSet);
	const visibleQuestions = questions?.slice(0, MAX_QUESTIONS) ?? [];

	return <PrintableLayout>
		<ScreenTitle title='Questions' />
		<LoadingOverlay loading={questions === undefined} label='Loading questions' />

		{(!questionSet || !questions || questions.empty())
			? <Typography>Choose a Question Set to see its questions</Typography>
			: <Box sx={styles.card}>
				<Box sx={styles.questionPanel}>
					<Header questionSet={questionSet} />

					<Box sx={styles.questionList}>
						{visibleQuestions.map((question, questionIndex) =>
							<Stack key={question.id} direction='row' spacing={1} sx={styles.questionBox}>
								<Typography sx={styles.questionNumber}>
									Q{questionIndex + 1}
								</Typography>
								<Typography sx={styles.questionText}>
									{question.text}
								</Typography>
							</Stack >
						)}
					</Box>
				</Box>

				<Box sx={styles.logoPanel}>
					<img alt={`${Environment.gameName} logo for back of card`} src={LOGO_PATH} style={styles.logoImage} />
				</Box>
			</Box>
		}
	</PrintableLayout>;
};

type HeaderProps = {
	questionSet: QuestionSetConfig;
};

/** Shows the selected Question Set details above the printable question list. */
const Header: FunctionComponent<HeaderProps> = (props) => {
	return <Box sx={styles.header}>
		<Typography sx={styles.questionSetName}>{props.questionSet.name}</Typography>
		{props.questionSet.description && <Typography sx={styles.questionSetDescription}>{props.questionSet.description}</Typography>}
		<Typography sx={styles.questionSetAuthor}>{authorDetails(props.questionSet)}</Typography>
	</Box>;
};

/** Formats the author metadata shown on the printable card. */
function authorDetails(questionSet: QuestionSetConfig) {
	let text = `Created by ${questionSet.authorName} on ${friendlyDateString({ millis: questionSet.createdAt })}`;
	if (questionSet.updatedAt !== questionSet.createdAt) {
		text += `. Updated ${friendlyDateString({ millis: questionSet.updatedAt })}`;
	}
	return text;
}

const styles = muiStyleSheet({
	card: theme => ({
		maxWidth: '210mm',
		marginX: 'auto',
		display: 'grid',
		gridTemplateRows: '1.45fr .55fr',
		border: `2px solid ${theme.palette.mind.ochre}`,
		borderRadius: 6,
		overflow: 'hidden',
		boxShadow: 6,
		backgroundColor: theme.palette.mind.white,
		printColorAdjust: 'exact',
		WebkitPrintColorAdjust: 'exact',
		'@media print': {
			height: '242mm',
			boxShadow: 'none',
			pageBreakInside: 'avoid',
		},
	}),
	questionPanel: theme => ({
		paddingX: 3,
		paddingY: 2.5,
		background: `linear-gradient(180deg, ${theme.palette.mind.sand} 0%, ${theme.palette.mind.white} 100%)`,
		'@media print': {
			paddingX: 2.5,
			paddingY: 2.25,
		},
	}),
	header: {
		marginX: 8,
		marginBottom: 2,
	},
	questionList: {
		display: 'grid',
		gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
		gap: 1.5,
		'@media (max-width: 700px)': {
			gridTemplateColumns: '1fr',
		},
	},
	questionSetName: {
		fontSize: '1.5rem',
		fontWeight: 'bold',
		lineHeight: 1.2,
		marginBottom: .5,
		'@media print': {
			fontSize: '1.35rem',
		},
	},
	questionSetDescription: {
		fontSize: '.92rem',
		fontStyle: 'italic',
		marginBottom: .35,
	},
	questionSetAuthor: {
		fontSize: '.78rem',
	},
	questionBox: theme => ({
		border: `2px solid ${theme.palette.mind.ochre}`,
		borderRadius: 1,
		paddingX: 4,
		paddingY: 1.25,
		backgroundColor: theme.palette.mind.white,
		pageBreakInside: 'avoid',
	}),
	questionNumber: theme => ({
		fontSize: '.8rem',
		fontWeight: 'bold',
		letterSpacing: '.12em',
		textTransform: 'uppercase',
		color: theme.palette.mind.clay,
		marginBottom: .25,
	}),
	questionText: {
		fontSize: '1.1rem',
		fontWeight: 'bold',
		lineHeight: 1.25,
		'@media print': {
			fontSize: '1rem',
		},
	},
	logoPanel: theme => ({
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		padding: 2.25,
		background: `linear-gradient(180deg, ${theme.palette.mind.jacaranda} 0%, ${theme.palette.mind.inky} 100%)`,
		transform: 'rotate(180deg)',
		'@media print': {
			padding: 2,
		},
	}),
	logoImage: {
		maxWidth: '100%',
		maxHeight: '100%',
		borderRadius: 60,
		objectFit: 'contain',
	},
});

